import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import adsApi from './adsApi';
import AdsImageField from './AdsImageField.jsx';
import AdsCitySearchSelect from './AdsCitySearchSelect.jsx';
const CAMPAIGN_TYPES = [
  'hero_banner',
  'mid_banner',
  'bottom_banner',
  'search_banner',
  'sponsored_listing',
  'offer_card',
  'coupon_card',
  'popup',
  'sticky_bottom',
  'floating_cta',
  'brand_landing',
  'seasonal',
];

const emptyCampaign = {
  name: '',
  advertiser_id: '',
  campaign_type: 'mid_banner',
  priority: 100,
  budget_amount: '',
  budget_currency: 'INR',
  start_at: '',
  end_at: '',
  timezone: 'Asia/Kolkata',
  status: 'draft',
  landing_page_url: '',
  internal_landing_slug: '',
  cta_text: 'Know More',
  cta_url: '',
  is_sponsored: false,
  placementCodes: ['home_mid'],
  pages: ['homepage'],
  locations: [],
  targeting: { devices: [], browsers: [], languages: [], auth_states: [] },
  banners: [
    {
      desktop_image_url: '',
      mobile_image_url: '',
      tablet_image_url: '',
      video_url: '',
      headline: '',
      subheadline: '',
      feature_text: '',
      alt_text: '',
      cta_text: 'Know More',
      cta_url: '',
    },
  ],
  pricing: [{ pricing_model: 'flat', amount: 0, currency: 'INR' }],
};

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdsCampaignEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyCampaign);
  const [advertisers, setAdvertisers] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const [adv, pl] = await Promise.all([adsApi.listAdvertisers({ limit: 500 }), adsApi.listPlacements()]);
        setAdvertisers(adv?.data?.items || []);
        setPlacements(pl?.data || []);
      } catch (err) {
        setError(err.message || 'Failed to load lookups');
      }
    })();
  }, []);

  useEffect(() => {
    if (isNew) return;
    void (async () => {
      setLoading(true);
      try {
        const res = await adsApi.getCampaign(id);
        const c = res?.data;
        if (!c) throw new Error('Campaign not found');
        setForm({
          name: c.name || '',
          advertiser_id: String(c.advertiser_id || ''),
          campaign_type: c.campaign_type || 'mid_banner',
          priority: c.priority ?? 100,
          budget_amount: c.budget_amount ?? '',
          budget_currency: c.budget_currency || 'INR',
          start_at: toLocalInput(c.start_at),
          end_at: toLocalInput(c.end_at),
          timezone: c.timezone || 'Asia/Kolkata',
          status: c.status || 'draft',
          landing_page_url: c.landing_page_url || '',
          internal_landing_slug: c.internal_landing_slug || '',
          cta_text: c.cta_text || 'Know More',
          cta_url: c.cta_url || '',
          is_sponsored: !!c.is_sponsored,
          placementCodes: (c.placements || []).map((p) => p.placement_code).filter(Boolean),
          pages: (c.pages || []).map((p) => p.page_key),
          locations: c.locations || [],
          targeting: c.targeting || { devices: [], browsers: [], languages: [], auth_states: [] },
          banners: c.banners?.length
            ? c.banners
            : emptyCampaign.banners,
          pricing: c.pricing?.length ? c.pricing : emptyCampaign.pricing,
        });
      } catch (err) {
        setError(err.message || 'Failed to load campaign');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const placementOptions = useMemo(() => placements, [placements]);

  const setBannerField = (field, value) => {
    setForm((f) => ({
      ...f,
      banners: f.banners.map((b, i) => (i === 0 ? { ...b, [field]: value } : b)),
    }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        advertiser_id: Number(form.advertiser_id),
        budget_amount: form.budget_amount === '' ? null : Number(form.budget_amount),
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        placementCodes: form.placementCodes,
      };
      if (isNew) {
        const res = await adsApi.createCampaign(payload);
        navigate(`/ads/campaigns/${res.data.id}`);
      } else {
        await adsApi.updateCampaign(id, payload);
        navigate('/ads/campaigns');
      }
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner size="large" message="Loading campaign..." />;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <h1 className="text-2xl font-bold text-slate-900">{isNew ? 'Create campaign' : 'Edit campaign'}</h1>
      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}
      <form onSubmit={onSave} className="space-y-6">
        <section className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-2">
          <input
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="Campaign name *"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <select
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            required
            value={form.advertiser_id}
            onChange={(e) => setForm((f) => ({ ...f, advertiser_id: e.target.value }))}
          >
            <option value="">Advertiser *</option>
            {advertisers.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <select
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            value={form.campaign_type}
            onChange={(e) => setForm((f) => ({ ...f, campaign_type: e.target.value }))}
          >
            {CAMPAIGN_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            {['draft', 'scheduled', 'active', 'paused', 'archived'].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            type="number"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="Priority"
            value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) }))}
          />
          <input
            type="number"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="Budget"
            value={form.budget_amount}
            onChange={(e) => setForm((f) => ({ ...f, budget_amount: e.target.value }))}
          />
          <input
            type="datetime-local"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            required
            value={form.start_at}
            onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))}
          />
          <input
            type="datetime-local"
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            required
            value={form.end_at}
            onChange={(e) => setForm((f) => ({ ...f, end_at: e.target.value }))}
          />
          <input
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="CTA text"
            value={form.cta_text}
            onChange={(e) => setForm((f) => ({ ...f, cta_text: e.target.value }))}
          />
          <input
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="Landing page URL"
            value={form.landing_page_url}
            onChange={(e) => setForm((f) => ({ ...f, landing_page_url: e.target.value }))}
          />
          <input
            className="rounded border border-slate-300 px-3 py-2 text-sm"
            placeholder="Internal landing slug"
            value={form.internal_landing_slug}
            onChange={(e) => setForm((f) => ({ ...f, internal_landing_slug: e.target.value }))}
          />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.is_sponsored}
              onChange={(e) => setForm((f) => ({ ...f, is_sponsored: e.target.checked }))}
            />
            Sponsored campaign
          </label>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <h2 className="font-semibold text-slate-900">Placements</h2>
          <div className="grid max-h-48 gap-2 overflow-y-auto md:grid-cols-3">
            {placementOptions.map((p) => (
              <label key={p.code} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.placementCodes.includes(p.code)}
                  onChange={(e) => {
                    setForm((f) => ({
                      ...f,
                      placementCodes: e.target.checked
                        ? [...f.placementCodes, p.code]
                        : f.placementCodes.filter((c) => c !== p.code),
                    }));
                  }}
                />
                {p.name}
              </label>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <h2 className="font-semibold text-slate-900">Banner creatives</h2>
          <div className="grid items-start gap-3 md:grid-cols-2">
            <input
              className="h-10 rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Headline"
              value={form.banners[0]?.headline || ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  banners: f.banners.map((b, i) => (i === 0 ? { ...b, headline: e.target.value } : b)),
                }))
              }
            />
            <input
              className="h-10 rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Feature text"
              value={form.banners[0]?.feature_text || ''}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  banners: f.banners.map((b, i) => (i === 0 ? { ...b, feature_text: e.target.value } : b)),
                }))
              }
            />
            <AdsImageField
              label="Desktop image"
              value={form.banners[0]?.desktop_image_url || ''}
              onChange={(path) => setBannerField('desktop_image_url', path)}
              onError={(msg) => setError(msg)}
            />
            <AdsImageField
              label="Mobile image"
              value={form.banners[0]?.mobile_image_url || ''}
              onChange={(path) => setBannerField('mobile_image_url', path)}
              onError={(msg) => setError(msg)}
            />
            <AdsImageField
              label="Tablet image"
              value={form.banners[0]?.tablet_image_url || ''}
              onChange={(path) => setBannerField('tablet_image_url', path)}
              onError={(msg) => setError(msg)}
            />
            <input
              className="h-10 rounded border border-slate-300 px-3 py-2 text-sm md:col-span-2"
              placeholder="Image alt text"
              value={form.banners[0]?.alt_text || ''}
              onChange={(e) => setBannerField('alt_text', e.target.value)}
            />
            <input
              className="h-10 self-start rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Video URL"
              value={form.banners[0]?.video_url || ''}
              onChange={(e) => setBannerField('video_url', e.target.value)}
            />
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <h2 className="font-semibold text-slate-900">Targeting</h2>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Cities</label>
            <AdsCitySearchSelect
              value={form.locations}
              onChange={(locations) => setForm((f) => ({ ...f, locations }))}
            />
          </div>
          <div className="grid gap-2 md:grid-cols-4">
            {['desktop', 'mobile', 'tablet'].map((d) => (
              <label key={d} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={(form.targeting.devices || []).includes(d)}
                  onChange={(e) => {
                    setForm((f) => {
                      const devices = new Set(f.targeting.devices || []);
                      if (e.target.checked) devices.add(d);
                      else devices.delete(d);
                      return { ...f, targeting: { ...f.targeting, devices: [...devices] } };
                    });
                  }}
                />
                {d}
              </label>
            ))}
            {['logged_in', 'guest'].map((a) => (
              <label key={a} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={(form.targeting.auth_states || []).includes(a)}
                  onChange={(e) => {
                    setForm((f) => {
                      const auth_states = new Set(f.targeting.auth_states || []);
                      if (e.target.checked) auth_states.add(a);
                      else auth_states.delete(a);
                      return { ...f, targeting: { ...f.targeting, auth_states: [...auth_states] } };
                    });
                  }}
                />
                {a}
              </label>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <h2 className="font-semibold text-slate-900">Pricing</h2>
          <div className="grid gap-3 md:grid-cols-3">
            <select
              className="rounded border border-slate-300 px-3 py-2 text-sm"
              value={form.pricing[0]?.pricing_model || 'flat'}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  pricing: [{ ...f.pricing[0], pricing_model: e.target.value }],
                }))
              }
            >
              {['monthly', 'weekly', 'daily', 'cpm', 'cpc', 'cpa', 'flat'].map((m) => (
                <option key={m} value={m}>
                  {m.toUpperCase()}
                </option>
              ))}
            </select>
            <input
              type="number"
              className="rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Amount"
              value={form.pricing[0]?.amount ?? 0}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  pricing: [{ ...f.pricing[0], amount: Number(e.target.value) }],
                }))
              }
            />
          </div>
        </section>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save campaign'}
          </button>
          <Link to="/ads/campaigns" className="rounded-md border border-slate-300 px-4 py-2 text-sm">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
